import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/layout/AppLayout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { motion } from 'framer-motion';
import {
  FileClock, Play, Trash2, Calendar, Clock, User, AlertTriangle,
} from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { interraiSteps } from '@/data/interraiQuestions';
import { toast } from 'sonner';

const totalQuestions = interraiSteps.reduce((s, sec) => s + sec.domande.length, 0);

export default function BozzeValutazioni() {
  const navigate = useNavigate();
  const { drafts, patients, deleteDraft } = useApp();

  const handleResume = (draft) => {
    navigate(`/medico/valutazione/${draft.patientId}`, {
      state: { resumeDraft: draft },
    });
  };

  const handleDelete = (draftId) => {
    deleteDraft(draftId);
    toast.info('Bozza eliminata', { description: 'La bozza è stata rimossa.' });
  };

  return (
    <AppLayout role="medico">
      <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-6" data-testid="bozze-valutazioni-page">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">Bozze Valutazioni interRAI</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Gestisci e completa le valutazioni salvate in precedenza</p>
          </div>
          {drafts.length > 0 && (
            <Badge className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-0 text-xs font-bold">
              {drafts.length} bozz{drafts.length === 1 ? 'a' : 'e'}
            </Badge>
          )}
        </div>

        {/* Empty State */}
        {drafts.length === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-20 text-center"
            data-testid="empty-state"
          >
            <div className="w-16 h-16 rounded-2xl bg-muted/50 flex items-center justify-center mb-4">
              <FileClock className="w-8 h-8 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-semibold text-foreground">Nessuna bozza in corso</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-xs">Quando interrompi una valutazione interRAI HC, la bozza apparirà qui per essere ripresa in seguito.</p>
          </motion.div>
        )}

        {/* Drafts List */}
        {drafts.length > 0 && (
          <div className="space-y-3">
            {drafts.map((draft, idx) => {
              const patient = patients.find(p => p.id === draft.patientId);
              if (!patient) return null;

              const answeredCount = Object.keys(draft.formData).length;
              const progressPct = Math.round((answeredCount / totalQuestions) * 100);
              const currentSectionName = interraiSteps[draft.lastSectionIdx]?.titolo || '';
              const isStale = draft.lastModified.includes('14/04');

              return (
                <motion.div
                  key={draft.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.06 }}
                  className="bg-card rounded-2xl border border-border shadow-sm hover:shadow-md transition-shadow overflow-hidden"
                  data-testid={`draft-card-${draft.id}`}
                >
                  <div className="p-5">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/15 flex items-center justify-center shrink-0">
                        <span className="text-sm font-bold text-primary">
                          {patient.cognome?.[0]}{patient.nome?.[0]}
                        </span>
                      </div>

                      {/* Patient Info & Progress */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-sm font-semibold text-foreground">{patient.cognome} {patient.nome}</p>
                          {isStale && (
                            <Badge className="bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 border-0 text-[9px] font-bold gap-1">
                              <AlertTriangle className="w-2.5 h-2.5" /> Da riprendere
                            </Badge>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground">
                          {patient.eta} anni &middot; {patient.codiceFiscale}
                        </p>

                        {/* Progress Section */}
                        <div className="mt-3 space-y-1.5">
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Avanzamento</span>
                            <span className="text-xs font-bold text-foreground">{progressPct}%</span>
                          </div>
                          <Progress value={progressPct} className="h-2" data-testid={`progress-${draft.id}`} />
                          <p className="text-[10px] text-muted-foreground">
                            Sezione {draft.completedSections + 1} di {interraiSteps.length} &middot; {answeredCount}/{totalQuestions} domande &middot; <span className="font-medium text-foreground/70">{currentSectionName}</span>
                          </p>
                        </div>

                        {/* Timestamps */}
                        <div className="flex items-center gap-4 mt-2.5 text-[10px] text-muted-foreground">
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Creata: {draft.createdAt}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> Ultima modifica: {draft.lastModified}</span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-2 shrink-0">
                        <Button
                          variant="outline"
                          className="gap-2 h-10 px-4 border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 font-semibold text-xs rounded-xl"
                          onClick={() => handleResume(draft)}
                          data-testid={`resume-btn-${draft.id}`}
                        >
                          <Play className="w-3.5 h-3.5" /> Riprendi Compilazione
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1.5 text-[10px] text-muted-foreground hover:text-destructive h-7"
                          onClick={() => handleDelete(draft.id)}
                          data-testid={`delete-btn-${draft.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Elimina bozza
                        </Button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}
